import { formatCandidateName } from "./candidateName.js";

// Alphabetical order for contacts, matching CandidateRepo.GetAlphaPageAsync:
//   1. People with both first and last name, by "Last, First"
//   2. Everyone else (unsplit import, single token, placeholder), by Name
//   3. Id as a stable tiebreak
//
// German collation, primary strength — case and diacritics do not change order
// ("Ä" ≈ "A", "Müller" ≈ "Muller"). Same rule as Mongo Collation("de", Primary).
// Sort lives in the UI so GetByProjectAsync can keep insertion order for chat,
// MCP, and anything else that is not the kanban.

export const ALPHA_NAME_FORMAT = "lastFirst";

const collator = new Intl.Collator("de", { sensitivity: "base" });

export function candidateAlphaParts(c) {
  const first = (c?.firstName ?? "").trim();
  const last = (c?.lastName ?? "").trim();
  if (first && last) return { tier: 0, key: `${last}, ${first}` };
  return { tier: 1, key: (c?.name ?? "").trim() };
}

export function compareCandidatesAlpha(a, b) {
  const pa = candidateAlphaParts(a);
  const pb = candidateAlphaParts(b);
  if (pa.tier !== pb.tier) return pa.tier - pb.tier;
  const byKey = collator.compare(pa.key, pb.key);
  if (byKey !== 0) return byKey;
  return (a?.id ?? "").localeCompare(b?.id ?? "");
}

export function sortByCandidateAlpha(items, getCandidate) {
  return [...items].sort((x, y) => compareCandidatesAlpha(getCandidate(x), getCandidate(y)));
}

export function sortProjectCandidatesAlpha(pcs, candidateMap = {}) {
  return sortByCandidateAlpha(pcs, (pc) => candidateMap[pc.candidateId] ?? { id: pc.candidateId });
}

export function sortIdsByCandidateAlpha(ids, candidateMap = {}) {
  return sortByCandidateAlpha(ids, (id) => candidateMap[id] ?? { id });
}

export function alphaDisplayName(c) {
  return formatCandidateName(c, ALPHA_NAME_FORMAT);
}

// Lowercased fields a type-ahead can match against when the list is last-first.
export function candidateAlphaHaystack(c) {
  if (!c) return "";
  return [c.lastName, c.firstName, c.name, alphaDisplayName(c)]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("de");
}
